/**
 * View 3, Bench strength: the supply of Ready Now talent against the approved
 * positions (HR, 9 Oct 2026: Ready Now people / approved positions). Ready Now
 * means ready to step in now: a Ready Now successor the room confirmed, or a next
 * role on someone's growth plan marked Ready now.
 */
import React, { useMemo } from 'react'
import type { ReportingRole } from '@/services/api/talentReportingService'
import { Panel, Tile, TileRow } from './TalentDashboardParts'
import { RoleTable, type RoleColumn } from './RoleTable'
import { CRITICAL_NO, CRITICAL_YES, LAYER_ORDER, fmtPct } from './talentDashboardData'
import { benchBy, benchOf, coverageRows, type Bench, type RoleCoverage } from './talentRoleData'

const strength = (b: Bench) => (b.strength === null ? 'n/a' : `${b.strength}%`)

function BenchTable({ rows, label }: { rows: { label: string; bench: Bench }[]; label: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[620px] border-collapse text-xs">
        <thead>
          <tr className="border-b border-gray-200 text-left text-[10px] uppercase tracking-wide text-gray-500">
            {[label, 'Roles', 'Positions', 'Headcount', 'Vacancies', 'Ready Now people', 'Bench strength'].map((h) => (
              <th key={h} scope="col" className="px-2 py-1.5 font-semibold">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(({ label: name, bench }) => (
            <tr key={name} className="border-b border-gray-100">
              <td className="px-2 py-1.5 text-gray-700">{name}</td>
              <td className="px-2 py-1.5 tabular-nums">{bench.roles}</td>
              <td className="px-2 py-1.5 tabular-nums">{bench.positions}</td>
              <td className="px-2 py-1.5 tabular-nums">{bench.filled}</td>
              <td className="px-2 py-1.5 tabular-nums">{bench.vacant}</td>
              <td className="px-2 py-1.5 tabular-nums">{bench.readyNow}</td>
              <td className="px-2 py-1.5">
                <span className="flex items-center gap-2">
                  <span className="h-2.5 w-24 rounded-sm bg-gray-100" aria-hidden="true">
                    <span className="block h-2.5 rounded-sm bg-heritage-blue/70" style={{ width: `${Math.min(bench.strength ?? 0, 100)}%` }} />
                  </span>
                  <span className="tabular-nums text-gray-700">{strength(bench)}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export const BenchView: React.FC<{
  roles: ReportingRole[]
  readyNow: Map<string, Set<string>>
  positionsNote: string
  onExport?: (rows: RoleCoverage[], what: string) => void
}> = ({ roles, readyNow, positionsNote, onExport }) => {
  const total = useMemo(() => benchOf(roles, readyNow), [roles, readyNow])
  const byLayer = useMemo(() => benchBy(roles, readyNow, (r) => r.leadership_layer, LAYER_ORDER), [roles, readyNow])
  const byFunction = useMemo(() => benchBy(roles, readyNow, (r) => r.function), [roles, readyNow])
  const byCritical = useMemo(
    () => benchBy(roles, readyNow, (r) => (r.critical ? CRITICAL_YES : CRITICAL_NO), [CRITICAL_YES, CRITICAL_NO]),
    [roles, readyNow],
  )
  const rows = useMemo(() => coverageRows(roles, new Map()), [roles])
  const ready = (c: RoleCoverage) => readyNow.get(c.key)?.size || 0
  const columns: RoleColumn[] = [
    { key: 'role', label: 'Role', render: (c) => c.role.role_title, sortValue: (c) => c.role.role_title },
    { key: 'function', label: 'Function', render: (c) => c.role.function || '', sortValue: (c) => c.role.function || '' },
    { key: 'layer', label: 'Leadership layer', render: (c) => c.role.leadership_layer || '', sortValue: (c) => c.role.leadership_layer || '' },
    { key: 'positions', label: 'Positions', render: (c) => c.role.positions, sortValue: (c) => c.role.positions, className: 'tabular-nums' },
    { key: 'filled', label: 'Headcount', render: (c) => c.role.filled, sortValue: (c) => c.role.filled, className: 'tabular-nums' },
    { key: 'vacant', label: 'Vacancies', render: (c) => c.role.vacant, sortValue: (c) => c.role.vacant, className: 'tabular-nums' },
    { key: 'ready', label: 'Ready Now people', render: ready, sortValue: ready, className: 'tabular-nums' },
    {
      key: 'strength',
      label: 'Bench strength',
      render: (c) => fmtPct(ready(c), c.role.positions),
      sortValue: (c) => ready(c) / Math.max(c.role.positions, 1),
      className: 'tabular-nums',
    },
  ]

  return (
    <div className="space-y-4">
      <TileRow min="160px">
        <Tile label="Positions" value={total.positions} sub="Approved, filled or vacant" />
        <Tile label="Headcount" value={total.filled} sub="People in positions" />
        <Tile label="Vacancies" value={total.vacant} sub="Approved but not filled" />
        <Tile label="Ready Now people" value={total.readyNow} sub="Ready to step into one of these roles now" />
        <Tile label="Bench strength" value={strength(total)} sub="Ready Now people per position" />
      </TileRow>
      <p className="text-[11px] text-gray-500">{positionsNote}</p>

      <Panel title="By leadership layer" note="Ready Now people per position, by the layer of the role they are ready for.">
        <BenchTable rows={byLayer} label="Leadership layer" />
      </Panel>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="By function"><BenchTable rows={byFunction} label="Function" /></Panel>
        <Panel title="Critical and other roles" note="Critical roles as flagged in Role Master.">
          <BenchTable rows={byCritical} label="Role group" />
        </Panel>
      </div>

      <RoleTable
        title="Roles, largest first"
        rows={[...rows].sort((a, b) => b.role.positions - a.role.positions)}
        columns={columns}
        onExport={onExport && (() => onExport(rows, 'Bench strength by role'))}
      />
    </div>
  )
}
